import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schema,
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const fullUrl = canonical || `${siteUrl}${location.pathname}`;

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Update basic meta tags
    updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    
    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullUrl);

    // Update last-modified
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // Update Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', fullUrl, true);

    // Update Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);

    // Add or update Schema.org JSON-LD
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      
      scriptTag.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // Remove schema script when component unmounts
      const scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (scriptTag) {
        scriptTag.remove();
      }
    };
  }, [title, description, keywords, fullUrl, ogType, schema]);
};

// Helper function to generate WebPage schema
export const generateWebPageSchema = (
  name: string,
  description: string,
  url: string
) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `${siteUrl}${url}`,
    publisher: {
      '@type': 'Organization',
      name: 'CerviShield',
      url: siteUrl,
    },
    inLanguage: 'en-IN',
  };
};

// Helper function to generate MedicalWebPage schema
export const generateMedicalWebPageSchema = (
  name: string,
  description: string,
  url: string,
  medicalAudience?: string[]
) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name,
    description,
    url: `${siteUrl}${url}`,
    publisher: {
      '@type': 'Organization',
      name: 'CerviShield',
      url: siteUrl,
    },
    inLanguage: 'en-IN',
    medicalAudience: medicalAudience || ['Patient'],
    about: {
      '@type': 'MedicalCondition',
      name: 'Cervical Cancer',
    },
  };
};

// Helper function to generate FAQPage schema
export const generateFAQPageSchema = (
  faqs: Array<{ question: string; answer: string }>
) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
    publisher: {
      '@type': 'Organization',
      name: 'CerviShield',
      url: siteUrl,
    },
  };
};

// Helper function to generate Organization schema
export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'CerviShield',
    url: siteUrl,
    description: 'AI-assisted cervical cancer awareness and risk guidance platform for early detection and prevention',
    foundingDate: '2024',
    areaServed: {
      '@type': 'Country',
      name: 'India',
    },
    knowsAbout: [
      'Cervical Cancer',
      'Cancer Prevention',
      'Health Screening',
      'Women\'s Health',
    ],
  };
};
